/* eslint-disable @typescript-eslint/no-explicit-any */
import {message, TableColumnsType} from "antd";
import CustomRangePicker from "@component/CustomRangePicker";
import CustomTable from "@component/CustomTable";
import IconTitle from "@icon/IconTitle";
import CustomButton from "@component/CustomButton";
import {
  SensorDetailItem,
  SensorDetailParam,
  SensorDetailSearchConditions,
} from "@interface/dashboard/Dashboard";
import { Option } from "antd/lib/mentions";
import { useEffect, useState } from "react";
import {
  getSensorDetail,
  getSensorDetailSearchConditions,
} from "@api/SensorDetail";
import { HttpStatusCode } from "axios";
import IconBtnSearch from "@icon/IconBtnSearch";
import CustomValidFormSelect from "@component/form/CustomValidFormSelect";
import { useForm } from "react-hook-form";
import CustomValidFormInput from "@component/form/CustomValidFormInput";
import dayjs from "dayjs";
import CustomValidFormCheckbox from "@component/form/CustomValidFormCheckbox";
import ExcelJS from "exceljs";
import { SENSOR_DATA_HEADER } from "@util/constants";

const SensorData = () => {
  const [open, setOpen] = useState(false);
  const [sensor, setSensor] = useState(true);
  const {
    control: searchFormControl,
    handleSubmit: searchFormHandleSubmit,
    setValue,
    getValues,
    watch,
  } = useForm<SensorDetailParam>({ mode: "all" });
  const watchStartDate = watch("startDate");
  const watchEndDate = watch("endDate");

  const [
    sensorDetailSearchConditionsState,
    setSensorDetailSearchConditionsState,
  ] = useState<SensorDetailSearchConditions>({
    sggList: [],
  });

  const [sensorDetailData, setSensorDetailData] = useState<SensorDetailItem[]>(
    []
  );

  const downloadExcel = async (type: string) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Sheet 1");
    const excelData = [
      { data: ["[검색조건]"] },
      { data: ["기간", getValues("startDate"), getValues("endDate")] },
      {
        data: [
          "권역",
          getValues("sggCd") === ""
            ? "전체"
            : sensorDetailSearchConditionsState.sggList.filter(
                (item) => item.value === getValues("sggCd")
              )[0].label,
        ],
      },
      { data: ["국가지점/다목적위치표지판 번호", getValues("lctnSignNo")] },
      { data: [""] },
      { data: SENSOR_DATA_HEADER },
    ];

    excelData.forEach((item, index) => {
      sheet.getRow(index + 1).values = [...item.data];
    });
    sheet.getRow(6).eachCell((cell: ExcelJS.Cell) => {
      cell.style.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
    });
    sensorDetailData.forEach((item: any, index) => {
      sheet.getRow(index + 7).values = [
        item.dateTime,
        item.ntnlPntNo,
        item.lctnSignNo,
        item.gpsLon,
        item.gpsLat,
        item.temp,
        item.hum,
        item.slope,
        item.shock,
        item.sound,
        item.battery1,
        item.battery2,
        item.battery3,
        item.usePower,
        item.gpsLonAlarmLevel,
        item.gpsLatAlarmLevel,
        item.tempAlarmLevel,
        item.humAlarmLevel,
        item.slopeAlarmLevel,
        item.shockAlarmLevel,
        item.soundAlarmLevel,
        item.battery1AlarmLevel,
        item.battery2AlarmLevel,
        item.battery3AlarmLevel,
      ];
      sheet.getRow(index + 7).eachCell((cell: ExcelJS.Cell) => {
        cell.style.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
      });
    });

    sheet.columns.forEach((column, index) => {
      const header = sheet
        .getRow(6)
        .getCell(index + 1)
        .value?.toString();
      if (index === 0) {
        column.width = 30;
      } else {
        column.width = header ? header.length + 10 : 12;
      }
    });

    let buffer;
    if (type === "01") {
      buffer = await workbook.csv.writeBuffer();
    } else {
      buffer = await workbook.xlsx.writeBuffer();
    }

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = type === "01" ? "센서데이터.csv" : "센서데이터.xlsx";
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  const dataSearch = (searchParam: SensorDetailParam) => {
    getSensorDetail(searchParam).then((res) => {
      if (res.code === HttpStatusCode.Ok && res.item) {
        setSensorDetailData(res.item);
      }
    });
  };

  const initDataSetting = () => {
    setValue("sggCd", "");
    setValue("startDate", dayjs().subtract(1, "month").format("YYYY-MM-DD"));
    setValue("endDate", dayjs().format("YYYY-MM-DD"));
    setValue("lctnSignNo", "");
    setValue("senSorStatus", "");
  };

  const sensorDataSearchConditions = () => {
    getSensorDetailSearchConditions().then((res) => {
      if (res.code === HttpStatusCode.Ok && res.item) {
        setSensorDetailSearchConditionsState(res.item);
        initDataSetting();
        dataSearch({
          startDate: dayjs().subtract(1, "month").format("YYYY-MM-DD"),
          endDate: dayjs().format("YYYY-MM-DD"),
          lctnSignNo: "",
          sggCd: "",
          senSorStatus: "",
        });
      }
    });
  };

  useEffect(() => {
    sensorDataSearchConditions();
  }, []);

  const sensorDetailColumns: TableColumnsType<SensorDetailItem> = [
    {
      title: "일자",
      dataIndex: "dateTime",
      key: "dateTimeDate",
      align: "center",
      width: 80,
      render: (value) => {
        return value ? dayjs(value).format("YYYY-MM-DD") : "";
      },
    },
    {
      title: "시간",
      dataIndex: "dateTime",
      key: "dateTimeTime",
      align: "center",
      width: 80,
      render: (value) => {
        return value ? dayjs(value).format("HH:mm") : "";
      },
    },
    {
      title: "국가지점번호",
      dataIndex: "ntnlPntNo",
      key: "ntnlPntNo",
      align: "center",
      width: 120,
    },
    {
      title: "다목적위치표지판번호",
      dataIndex: "lctnSignNo",
      key: "lctnSignNo",
      align: "center",
      width: 150,
    },
    {
      title: "GNSS-경도",
      dataIndex: "gpsLon",
      key: "gpsLon",
      align: "center",
      width: 100,
    },
    {
      title: "GNSS-위도",
      dataIndex: "gpsLat",
      key: "gpsLat",
      align: "center",
      width: 100,
    },
    {
      title: "온도",
      dataIndex: "temp",
      key: "temp",
      align: "center",
      width: 80,
      render: (value, record: any) => {
        if (value) {
          const levelArr =
              record.tempAlarmLevel && record.tempAlarmLevel.split(":");
          if (levelArr && levelArr[0] > value && levelArr[1] < value) {
            return <p>{value}</p>;
          } else {
            return <p style={{ color: "red" }}>{value}</p>;
          }
        } else {
          return "";
        }
      },
    },
    {
      title: "습도",
      dataIndex: "hum",
      key: "hum",
      align: "center",
      width: 80,
      render: (value, record: any) => {
        if (value) {
          const levelArr =
              record.humAlarmLevel && record.humAlarmLevel.split(":");
          if (levelArr && levelArr[0] > value && levelArr[1] < value) {
            return <p>{value}</p>;
          } else {
            return <p style={{ color: "red" }}>{value}</p>;
          }
        } else {
          return "";
        }
      },
    },
    {
      title: "기울기",
      dataIndex: "slope",
      key: "slope",
      align: "center",
      width: 120,
      render: (value, record: any) => {
        if (value) {
          const valueArr = value && value.split(":");
          const levelArr =
              record.slopeAlarmLevel && record.slopeAlarmLevel.split(":");
          if (
              levelArr &&
              valueArr &&
              (levelArr[0] < valueArr[0] ||
                  -levelArr[0] > valueArr[0] ||
                  levelArr[1] < valueArr[1] ||
                  -levelArr[1] > valueArr[1] ||
                  levelArr[2] < valueArr[2]||
                  -levelArr[2] > valueArr[2])
          ) {
            return <p style={{ color: "red" }}>{value}</p>;
          } else {
            return value;
          }
        } else {
          return "";
        }
      },
    },
    {
      title: "충격",
      dataIndex: "shock",
      key: "shock",
      align: "center",
      width: 80,
      render: (value, record: any) => {
        if (value > record.shockAlarmLevel) {
          return <p style={{ color: "red" }}>{value}</p>;
        } else {
          return <p>{value}</p>;
        }
      },
    },
    {
      title: "소리",
      dataIndex: "sound",
      key: "sound",
      align: "center",
      width: 80,
      render: (value, record: any) => {
        if (value > record.soundAlarmLevel) {
          return <p style={{ color: "red" }}>{value}</p>;
        } else {
          return <p>{value}</p>;
        }
      },
    },
    {
      title: "배터리1",
      dataIndex: "battery1",
      key: "battery1",
      align: "center",
      width: 80,
      render: (value, record: any) => {
        if (value > record.battery1AlarmLevel) {
          return <p>{value}</p>;
        } else {
          return <p style={{ color: "red" }}>{value}</p>;
        }
      },
    },
    {
      title: "배터리2",
      dataIndex: "battery2",
      key: "battery2",
      align: "center",
      width: 80,
      render: (value, record: any) => {
        if (value > record.battery2AlarmLevel) {
          return <p>{value}</p>;
        } else {
          return <p style={{ color: "red" }}>{value}</p>;
        }
      },
    },
    {
      title: "배터리3",
      dataIndex: "battery3",
      key: "battery3",
      align: "center",
      width: 80,
      render: (value, record: any) => {
        if (value > record.battery3AlarmLevel) {
          return <p>{value}</p>;
        } else {
          return <p style={{ color: "red" }}>{value}</p>;
        }
      },
    },
    {
      title: "Use-Power",
      dataIndex: "usePower",
      key: "usePower",
      align: "center",
      width: 100,
    },
    {
      title: "GNSS-경도 레벨",
      dataIndex: "gpsLonAlarmLevel",
      key: "gpsLonAlarmLevel",
      align: "center",
      width: 150,
    },
    {
      title: "GNSS-위도 레벨",
      dataIndex: "gpsLatAlarmLevel",
      key: "gpsLatAlarmLevel",
      align: "center",
      width: 150,
    },
    {
      title: "온도 레벨",
      dataIndex: "tempAlarmLevel",
      key: "tempAlarmLevel",
      align: "center",
      width: 130,
    },
    {
      title: "습도 레벨",
      dataIndex: "humAlarmLevel",
      key: "humAlarmLevel",
      align: "center",
      width: 130,
    },
    {
      title: "기울기 레벨",
      dataIndex: "slopeAlarmLevel",
      key: "slopeAlarmLevel",
      align: "center",
      width: 120,
    },
    {
      title: "충격 레벨",
      dataIndex: "shockAlarmLevel",
      key: "shockAlarmLevel",
      align: "center",
      width: 80,
    },
    {
      title: "소리 레벨",
      dataIndex: "soundAlarmLevel",
      key: "soundAlarmLevel",
      align: "center",
      width: 80,
    },
    {
      title: "배터리1 레벨",
      dataIndex: "battery1AlarmLevel",
      key: "battery1AlarmLevel",
      align: "center",
      width: 120,
    },
    {
      title: "배터리2 레벨",
      dataIndex: "battery2AlarmLevel",
      key: "battery2AlarmLevel",
      align: "center",
      width: 120,
    },
    {
      title: "배터리3 레벨",
      dataIndex: "battery3AlarmLevel",
      key: "battery3AlarmLevel",
      align: "center",
      width: 120,
    },
  ];

  // 가상 스크롤(virtual)은 scroll.x에 숫자 너비가 필요 (45 = CustomTable이 추가하는 NO 컬럼 너비)
  const tableWidth =
    45 +
    sensorDetailColumns.reduce((sum, col) => sum + Number(col.width ?? 100), 0);

  return (
    <>
      <section className="title-wrap">
        <div className="box-flex">
          <h2 className="title">센서데이터</h2>
        </div>
      </section>
      <section className="button-wrap">
        <div className="box-btn">
          <CustomButton
            type="primary"
            onClick={searchFormHandleSubmit(dataSearch)}
          >
            <IconBtnSearch />
            조회
          </CustomButton>
        </div>
      </section>
      <section className="search-wrap">
        <form>
          <div className="deps">
            <span>기간</span>
            <CustomRangePicker
              open={open}
              onOpenChange={(open) => setOpen(open)}
              value={[
                watchStartDate ? dayjs(watchStartDate) : null,
                watchEndDate ? dayjs(watchEndDate) : null,
              ]}
              minLength={6}
              onChange={(value) => {
                if (!value || !value[0] || !value[1]) {
                  setValue("startDate", "");
                  setValue("endDate", "");
                  return;
                }
                const maxDate = dayjs().subtract(6, "month");
                if (value[0].isBefore(maxDate) || value[1].isBefore(maxDate)) {
                  message.error("최대 6개월까지만 조회 가능합니다.");
                  setOpen(false);
                  return;
                }
                setValue("startDate", value[0].format("YYYY-MM-DD"));
                setValue("endDate", value[1].format("YYYY-MM-DD"));
              }}
            />

            <span>권역</span>
            <CustomValidFormSelect control={searchFormControl} name={"sggCd"}>
              <Option value={""}>전체</Option>
              {sensorDetailSearchConditionsState.sggList.map((item, index) => {
                return (
                  <Option key={`sgg_${index}`} value={item.value}>
                    {item.label}
                  </Option>
                );
              })}
            </CustomValidFormSelect>

            <span>국가지점/다목적위치표지판 번호</span>
            <CustomValidFormInput
              name={"lctnSignNo"}
              control={searchFormControl}
            />
          </div>
          <div className="deps">
            <span>센서 상태</span>
            <CustomValidFormSelect
              control={searchFormControl}
              name={"senSorStatus"}
              onChangeValueback={(v)=>{
                if(v!="false")
                {setSensor(true);
                  setValue("sensorType",null);}
                else
                {setSensor(false);}

              }}
            >
              <Option value={""}>전체</Option>
              <Option value={"true"}>정상</Option>
              <Option value={"false"}>비정상</Option>
            </CustomValidFormSelect>

            <span>센서 종류</span>
            <CustomValidFormSelect
              control={searchFormControl}
              name={"sensorType"}
              disabled={sensor}
              mode="multiple"
              style={{ width: "263px" }}
            >
              <Option value={"temp"}>온도</Option>
              <Option value={"hum"}>습도</Option>
              <Option value={"gps"}>GNSS</Option>
              <Option value={"slope"}>기울기</Option>
              <Option value={"shock"}>충격</Option>
              <Option value={"sound"}>소리</Option>
              <Option value={"battery1"}>배터리1</Option>
              <Option value={"battery2"}>배터리2</Option>
              <Option value={"battery3"}>배터리3</Option>
            </CustomValidFormSelect>

            <CustomValidFormCheckbox
              control={searchFormControl}
              name={"accdntAreaFlag"}
            />
            <span>사고다발지역</span>
          </div>
        </form>
      </section>
      <section className="board-wrap">
        <div>
          <div className="board-title-wrap">
            <h3 className="title">
              <IconTitle />
              목록
            </h3>
            <div className="box-btn">
              <CustomButton
                type="default"
                size="small"
                onClick={() => downloadExcel("01")}
              >
                CSV
              </CustomButton>
              <CustomButton
                type="default"
                size="small"
                onClick={() => downloadExcel("02")}
              >
                EXCEL
              </CustomButton>
            </div>
          </div>
          <div className="board-cont-wrap">
            <CustomTable
              className="table-type-inp"
              rowKey={"rowNo"}
              columns={sensorDetailColumns}
              dataSource={sensorDetailData}
              rowNoFlag={true}
              virtual
              scroll={{ x: tableWidth }}
            />
          </div>
        </div>
      </section>
    </>
  );
};
export default SensorData;
